import React from "react";

import type {
  File,
  PromptResponsePage,
} from "@alaffia-technology-solutions/schema";

import {
  BadgeCitationKey,
  badgeCitationVariants,
  Button,
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@alaffia-technology-solutions/ds";

import { BookMarked, ChevronDown, ChevronUp } from "lucide-react";

export interface AnnotationCoords {
  height: number;
  left: number;
  top: number;
  width: number;
}

interface Citation {
  fileId: string;
  page: number;
  citationKey?: string;
}

interface OnFilePreviewProps {
  fileId: PromptResponsePage["file"]["id"];
  page?: PromptResponsePage["pageNumber"];
  annotations?: AnnotationCoords[];
  highlights?: string[];
}

export type OnFilePreview = (props: OnFilePreviewProps) => void;

const CITATIONS_LIMIT = 4;

const CitationBadge = ({
  citation,
  onFilePreviewCallback,
}: {
  citation: Citation;
  onFilePreviewCallback: OnFilePreview;
}) => {
  return (
    <button
      className={badgeCitationVariants()}
      onClick={() => {
        onFilePreviewCallback({
          fileId: citation.fileId,
          page: citation.page,
        });
      }}
    >
      <>
        {citation.citationKey && (
          <BadgeCitationKey>{citation.citationKey}</BadgeCitationKey>
        )}
        p. {citation.page}
      </>
    </button>
  );
};

interface CitationBadgesProps {
  citations: { citationKey?: string; page: number }[];
  onFilePreviewCallback: OnFilePreview;
  fileId: string;
}
const CitationBadges = React.memo<CitationBadgesProps>(
  ({ citations, onFilePreviewCallback, fileId }) => {
    const [isExpanded, setIsExpanded] = React.useState(false);

    const initialCitations = React.useMemo(() => {
      return citations.slice(0, CITATIONS_LIMIT);
    }, [citations]);

    const remainingCitations = React.useMemo(() => {
      return citations.slice(CITATIONS_LIMIT);
    }, [citations]);

    const hasMore = citations.length > CITATIONS_LIMIT;

    return (
      <Collapsible open={isExpanded} onOpenChange={setIsExpanded}>
        <ul className="af-flex af-list-none af-flex-wrap af-gap-1.5 af-p-0">
          {initialCitations.map((citation, index) => (
            <li key={`${fileId}-${citation.page}-${index}`}>
              <CitationBadge
                citation={{
                  fileId,
                  page: citation.page,
                  citationKey: citation.citationKey,
                }}
                onFilePreviewCallback={onFilePreviewCallback}
              />
            </li>
          ))}

          <CollapsibleContent
            asChild
            className="af-flex af-list-none af-flex-wrap af-gap-1.5 af-p-0"
          >
            <>
              {remainingCitations.map((citation, index) => (
                <li
                  key={`${fileId}-${citation.page}-${index + CITATIONS_LIMIT}`}
                >
                  <CitationBadge
                    citation={{
                      fileId,
                      page: citation.page,
                      citationKey: citation.citationKey,
                    }}
                    onFilePreviewCallback={onFilePreviewCallback}
                  />
                </li>
              ))}
            </>
          </CollapsibleContent>

          {hasMore && (
            <li className="af-flex af-items-center">
              <CollapsibleTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  className="af-h-6 af-px-2 af-text-xs af-text-primary"
                >
                  {isExpanded ? (
                    <>Show less</>
                  ) : (
                    <>Show {citations.length - initialCitations.length} more</>
                  )}
                </Button>
              </CollapsibleTrigger>
            </li>
          )}
        </ul>
      </Collapsible>
    );
  },
);

export interface FileWithCitations {
  fileId: File["id"];
  fileName: File["fileName"];
  citations: {
    citationKey?: string;
    page: number;
  }[];
}

interface CitationsSourcesProps extends React.PropsWithChildren {
  citations: FileWithCitations[];
  onFilePreviewCallback: OnFilePreview;
}
export const CitationsSources = ({
  children,
  citations,
  onFilePreviewCallback,
}: CitationsSourcesProps) => {
  const [isOpen, setIsOpen] = React.useState(false);

  const filesCount = citations.length;
  const citationsCount = citations.reduce(
    (total, file) => total + file.citations.length,
    0,
  );

  return (
    <Collapsible
      className="af-grid af-w-full af-gap-2"
      open={isOpen}
      onOpenChange={setIsOpen}
    >
      <div className="af-flex af-justify-between af-gap-2">
        <CollapsibleTrigger className="af-flex af-items-center af-gap-2 af-py-1.5 af-text-sm af-text-muted-foreground">
          <span>
            Sources ({filesCount} {filesCount === 1 ? "document" : "documents"},{" "}
            {citationsCount} {citationsCount === 1 ? "citation" : "citations"})
          </span>
          {isOpen ? (
            <ChevronUp className="af-h-4 af-w-4" />
          ) : (
            <ChevronDown className="af-h-4 af-w-4" />
          )}
        </CollapsibleTrigger>
        {children}
      </div>
      <CollapsibleContent>
        <ul className="af-flex af-list-none af-flex-col af-gap-2 af-p-0">
          {citations.map((file) => (
            <li
              key={file.fileId}
              className="af-flex af-flex-wrap af-items-center af-gap-1.5"
            >
              <button
                className="af-flex af-min-h-6 af-items-center af-gap-1 af-text-xs af-text-foreground hover:af-underline"
                onClick={() => {
                  onFilePreviewCallback({
                    fileId: file.fileId,
                    page: 1,
                  });
                }}
              >
                <BookMarked className="af-size-3" />
                {file.fileName}
              </button>

              <CitationBadges
                citations={file.citations}
                onFilePreviewCallback={onFilePreviewCallback}
                fileId={file.fileId}
              />
            </li>
          ))}
        </ul>
      </CollapsibleContent>
    </Collapsible>
  );
};
